import React, { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Oval } from "react-loader-spinner";
import { useLocation } from 'react-router-dom';
import "./PublicServiceApproval.css";
import configs from "../../../configs";
import { BreakdownApis } from "../../../apis/breakdownApis";
import { AxiosResponse } from "axios";
import { convertUTCDateToLocalDate } from '../../../utils/functions';
import DocumentViewerModal from "../../shared/DocumentViewerModal";

/* ─────────────────────────────────────────
   Icons
───────────────────────────────────────── */
const CheckCircleIcon = ({ color = "#111827" }: { color?: string }) => (
    <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="8.25" stroke={color} strokeWidth="1.5" />
        <path d="M5.5 9L7.8 11.5L12.5 6.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M3 5l4 4 4-4" stroke="#3F83F8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M5 5l10 10M15 5L5 15" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const TrashIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="2" width="16" height="16" rx="2" fill="#FEE2E2" />
        <path d="M5 5h10M8 5V3.5h4V5M6.5 5l.4 9h6.2L13.5 5" stroke="#EF4444" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────
   Component
───────────────────────────────────────── */
const PublicServiceAssetApproval: React.FC = () => {

    const location = useLocation();
    const sigPad = useRef<SignatureCanvas>(null);

    /* ── Query params ── */
    const queryParams = new URLSearchParams(location.search);
    const scheduleAssetId = queryParams.get('scheduleAssetId') || '';
    const expiry = queryParams.get('expiry') || '';
    const token = queryParams.get('token') || '';

    /* ── Page states ── */
    const [pageLoader, setPageLoader] = useState<boolean>(true);
    const [pageError, setPageError] = useState<string>('');
    const [scheduleData, setScheduleData] = useState<any>(null);
    const [alreadyApproved, setAlreadyApproved] = useState<boolean>(false);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

    /* ── UI states ── */
    const [checklistOpen, setChecklistOpen] = useState<boolean>(true);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    /* ── Document viewer states ── */
    const [docViewer, setDocViewer] = useState<{ open: boolean; url: string; type: string; name: string }>({ open: false, url: '', type: '', name: '' });
    const openDocViewer = (filePath: string, fileType: string, name: string = 'Checklist Document') => {
        if (!filePath) return;
        setDocViewer({ open: true, url: `${configs?.filePath}/${filePath}`, type: fileType || '', name });
    };
    const closeDocViewer = () => setDocViewer((prev) => ({ ...prev, open: false }));

    /* ── Modal states ── */
    const [decisionType, setDecisionType] = useState<"approve" | "clarify">("approve");
    const [approveStep, setApproveStep] = useState<1 | 2>(1);
    const [approverName, setApproverName] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const [remark, setRemark] = useState<string>("");
    const [clarifyName, setClarifyName] = useState<string>("");
    const [clarifyQuestion, setClarifyQuestion] = useState<string>("");
    const [submitLoader, setSubmitLoader] = useState<boolean>(false);

    /* ── Fetch on mount ── */
    useEffect(() => {
        if (!scheduleAssetId || !expiry || !token) {
            setPageError('Invalid or missing approval link parameters.');
            setPageLoader(false);
            return;
        }

        BreakdownApis.getPublicScheduleAsset(scheduleAssetId, expiry, token)
            .then((response: AxiosResponse<any>) => {
                if (response?.data) {
                    setScheduleData(response.data);
                    if (response.data.approveBy) {
                        setAlreadyApproved(true);
                    }
                }
            })
            .catch((err: any) => {
                const status = err?.response?.status;
                const responseData = err?.response?.data;
                const errorMessage = (responseData?.ErrorMessage || '').toLowerCase();
            
                if (errorMessage.includes('already used')) {
                    setAlreadyApproved(true);
                } else if (status === 401 || status === 403) {
                    setPageError('This approval link has expired or is no longer valid.');
                } else {
                    setPageError('Unable to load service details. Please try again or contact support.');
                }
            })
            .finally(() => setPageLoader(false));
    }, []);

    /* ── Modal helpers ── */
    const openModal = () => {
        setModalOpen(true);
        setDecisionType("approve");
        setApproveStep(1);
    };

    const closeModal = () => {
        setModalOpen(false);
        setApproveStep(1);
        setApproverName("");
        setPosition("");
        setRemark("");
        setClarifyName("");
        setClarifyQuestion("");
        sigPad.current?.clear();
    };

    const handleConfirmApproval = () => {
        if (approverName.trim() && position.trim()) setApproveStep(2);
    };

    const handleApproveService = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitLoader(true);

        if (!sigPad.current) return;

        sigPad.current.getTrimmedCanvas().toBlob((blob) => {
            const imageFile = new File([blob!], 'signature.png', { type: 'image/png' });
            const formData = new FormData();
            formData.append('ApproveBy', approverName);
            formData.append('ApprovalPosition', position);
            formData.append('Signature', imageFile);
            formData.append('Comment', remark);

            BreakdownApis.approvePublicScheduleAsset(scheduleAssetId, expiry, token, formData)
                .then(() => {
                    setSubmitSuccess(true);
                    closeModal();
                })
                .catch(() => {
                    setPageError('Failed to submit approval. Please try again.');
                })
                .finally(() => setSubmitLoader(false));
        }, 'image/png');
    };

    const handleSendClarification = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitLoader(true);
        // Placeholder — no endpoint yet
        setTimeout(() => {
            setSubmitLoader(false);
            closeModal();
        }, 1500);
    };

    const canConfirm = approverName.trim().length > 0 && position.trim().length > 0;

    /* ── Render: Loading ── */
    if (pageLoader) {
        return (
            <>
                <nav className="psa-navbar">
                    <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                </nav>
                <div className="psa-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
                    <div style={{ textAlign: 'center' }}>
                        <Oval visible height="40" width="40" color="#3F83F8" secondaryColor="#CAD8FA" ariaLabel="loading" />
                        <p style={{ marginTop: '16px', color: '#6B7280', fontSize: '14px' }}>Loading service details...</p>
                    </div>
                </div>
            </>
        );
    }

    /* ── Render: Error ── */
    if (pageError && !submitSuccess) {
        return (
            <>
                <nav className="psa-navbar">
                    <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                </nav>
                <div className="psa-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
                    <div style={{ textAlign: 'center', maxWidth: '400px', padding: '0 20px' }}>
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ margin: '0 auto 16px', display: 'block' }}>
                            <circle cx="32" cy="32" r="30" fill="#FEF2F2" stroke="#FECACA" strokeWidth="2" />
                            <path d="M32 20v16M32 42v2" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                        <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Link Unavailable</h2>
                        <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6' }}>{pageError}</p>
                    </div>
                </div>
            </>
        );
    }

    /* ── Render: Already Approved ── */
    if (alreadyApproved) {
        return (
            <>
                <nav className="psa-navbar">
                    <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                    <span className="psa-navbar-badge" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>Approved</span>
                </nav>
                <div className="psa-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
                    <div style={{ textAlign: 'center', maxWidth: '400px', padding: '0 20px' }}>
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ margin: '0 auto 16px', display: 'block' }}>
                            <circle cx="32" cy="32" r="30" fill="#D1FAE5" stroke="#6EE7B7" strokeWidth="2" />
                            <path d="M20 32l8 8 16-16" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Already Approved</h2>
                        <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6' }}>
                            This service has already been approved by a supervisor.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    /* ── Render: Submit Success ── */
    if (submitSuccess) {
        return (
            <>
                <nav className="psa-navbar">
                    <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                    <span className="psa-navbar-badge" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>Approved</span>
                </nav>
                <div className="psa-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
                    <div style={{ textAlign: 'center', maxWidth: '400px', padding: '0 20px' }}>
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ margin: '0 auto 16px', display: 'block' }}>
                            <circle cx="32" cy="32" r="30" fill="#D1FAE5" stroke="#6EE7B7" strokeWidth="2" />
                            <path d="M20 32l8 8 16-16" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Approval Submitted</h2>
                        <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6' }}>
                            Thank you! Your approval for this service has been recorded successfully.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    /* ── Render: Main page ── */
    return (
        <>
            <nav className="psa-navbar">
                <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                <span className="psa-navbar-badge">Pending Approval</span>
            </nav>

            <div className="psa-body">
                <div className="psa-content">
                    <h1 className="psa-title">Request Service Asset Approval</h1>
                    <p className="psa-subtitle">Review the service details below before submitting your decision.</p>

                    {/* ── Service Overview ── */}
                    <div className="psa-card">
                        <h2 className="psa-card-title">Service Overview</h2>
                        <div className="psa-overview-grid">
                            <div>
                                <p className="psa-field-label">Asset Name</p>
                                <p className="psa-field-value">{scheduleData?.asset?.name || '—'}</p>
                            </div>
                            <div>
                                <p className="psa-field-label">Contractor Name</p>
                                <p className="psa-field-value">{scheduleData?.contractorName || '—'}</p>
                            </div>
                            <div>
                                <p className="psa-field-label">Location</p>
                                <p className="psa-field-value">{scheduleData?.asset?.location || '—'}</p>
                            </div>
                            <div>
                                <p className="psa-field-label">Manufacturer</p>
                                <p className="psa-field-value">{scheduleData?.asset?.manufacturer || '—'}</p>
                            </div>
                            <div>
                                <p className="psa-field-label">Model</p>
                                <p className="psa-field-value">{scheduleData?.asset?.model || '—'}</p>
                            </div>
                            <div>
                                <p className="psa-field-label">Status</p>
                                <p className="psa-field-value">{scheduleData?.status || '—'}</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Service Checklist ── */}
                    {scheduleData?.checklistItem?.length > 0 && (
                        <div className="psa-card">
                            <div className="psa-checklist-header">
                                <div className="psa-checklist-title-wrap">
                                    <CheckCircleIcon />
                                    <h2 className="psa-checklist-title">Service Checklist</h2>
                                </div>
                                <span
                                    className={`psa-view-details-btn${checklistOpen ? " open" : ""}`}
                                    onClick={() => setChecklistOpen(!checklistOpen)}
                                >
                                    View Details <ChevronDownIcon />
                                </span>
                            </div>

                            {checklistOpen && (
                                <div className="psa-checklist-items mt-4">
                                    {scheduleData.checklistItem.map((item: any, i: number) => (
                                        <div className="psa-checklist-item" key={i} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                <span className="psa-checklist-item-left">
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M6.72254 11.1372L3.63672 8.05066L4.66508 7.0223L6.72254 9.07903L10.836 4.96484L11.8651 5.99393L6.72254 11.1372Z" fill="black" />
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M0 8C0 3.58182 3.58182 0 8 0C12.4182 0 16 3.58182 16 8C16 12.4182 12.4182 16 8 16C3.58182 16 0 12.4182 0 8ZM8 14.5455C4.39 14.5455 1.45455 11.61 1.45455 8C1.45455 4.39 4.39 1.45455 8 1.45455C11.61 1.45455 14.5455 4.39 14.5455 8C14.5455 11.61 11.61 14.5455 8 14.5455Z" fill="black" />
                                                    </svg>
                                                    {item.toDo}
                                                </span>
                                                <span className={item.isDone ? "psa-done-badge" : "psa-pending-badge"}>
                                                    {item.isDone ? 'Done' : 'Pending'}
                                                </span>
                                            </div>

                                            {item.filePath && (
                                                <div
                                                    onClick={() => openDocViewer(item.filePath, item.fileType)}
                                                    style={{
                                                        marginTop: '10px', marginLeft: '26px', padding: '8px 12px',
                                                        backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px',
                                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer',
                                                    }}
                                                >
                                                    <span style={{ fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                                            <path d="M4 3.5h8l4 4V16.5H4z" stroke="#3F83F8" strokeWidth="1.4" strokeLinejoin="round" />
                                                            <path d="M12 3.5v4h4" stroke="#3F83F8" strokeWidth="1.4" strokeLinejoin="round" />
                                                        </svg>
                                                        Checklist Document{item.fileType ? ` (.${item.fileType})` : ''}
                                                    </span>
                                                    <span style={{ color: '#3F83F8', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                                                            <path d="M2 10s3-5.5 8-5.5S18 10 18 10s-3 5.5-8 5.5S2 10 2 10z" stroke="#3F83F8" strokeWidth="1.4" />
                                                            <circle cx="10" cy="10" r="2.2" stroke="#3F83F8" strokeWidth="1.4" />
                                                        </svg>
                                                        View
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Signatures ── */}
                    <div className="psa-card">
                        <h2 className="psa-card-title">Signatures</h2>
                        <div className="psa-attachment-grid">
                            <div>
                                <h3 className="psa-card-subtitle">Service Approved By</h3>
                                <div className="psa-attachment-item">
                                    {scheduleData?.approvalSignature
                                        ? <img src={`${configs?.filePath}/${scheduleData?.approvalSignature}`} style={{ height: '50px', width: '126px' }} alt="approval signature" />
                                        : <div className="py-2" style={{ fontWeight: '500', color: 'black', fontSize: '14px' }}>No Image Available</div>
                                    }
                                </div>
                            </div>
                            <div>
                                <h3 className="psa-card-subtitle">Serviced By</h3>
                                <div className="psa-attachment-item">
                                    {scheduleData?.contractorSignature
                                        ? <img src={`${configs?.filePath}/${scheduleData?.contractorSignature}`} style={{ height: '50px', width: '126px' }} alt="contractor signature" />
                                        : <div className="py-2" style={{ fontWeight: '500', color: 'black', fontSize: '14px' }}>No Image Available</div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    <button className="psa-approve-btn" onClick={openModal}>
                        Approve Service
                    </button>
                </div>
            </div>

            {/* ══ DECISION MODAL ══ */}
            {modalOpen && (
                <div className="psa-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
                    <div className="psa-modal">

                        <div className="psa-modal-header">
                            <h3 className="psa-modal-title">Your Decision</h3>
                            <button className="psa-modal-close" onClick={closeModal}><CloseIcon /></button>
                        </div>

                        {/* <div className="psa-decision-toggle">
                            <button
                                className={`psa-toggle-btn${decisionType === "approve" ? " active" : ""}`}
                                onClick={() => { setDecisionType("approve"); setApproveStep(1); }}
                            >
                                Approve
                            </button>
                            <button
                                className={`psa-toggle-btn${decisionType === "clarify" ? " active" : ""}`}
                                onClick={() => { setDecisionType("clarify"); setApproveStep(1); }}
                            >
                                Need Clarification
                            </button>
                        </div> */}

                        {/* ── APPROVE flow ── */}
                        {decisionType === "approve" && (
                            <>
                                <div className="psa-steps">
                                    <div className="psa-step-item">
                                        <div className={`psa-step-bubble${approveStep > 1 ? " done" : " active"}`}>
                                            {approveStep > 1
                                                ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                : "1"}
                                        </div>
                                        <span className={`psa-step-label${approveStep === 1 ? " active" : " inactive"}`}>Approver ID</span>
                                    </div>
                                    <div className={`psa-step-connector${approveStep > 1 ? " done" : ""}`} />
                                    <div className="psa-step-item">
                                        <div className={`psa-step-bubble${approveStep === 2 ? " active" : " inactive"}`}>2</div>
                                        <span className={`psa-step-label${approveStep === 2 ? " active" : " inactive"}`}>Confirmation</span>
                                    </div>
                                </div>

                                {approveStep === 1 && (
                                    <>
                                        <div className="psa-form-group">
                                            <label className="psa-form-label">Approver Name</label>
                                            <input
                                                type="text"
                                                className="psa-form-input"
                                                placeholder="Enter Approver Name"
                                                value={approverName}
                                                onChange={(e) => setApproverName(e.target.value)}
                                            />
                                        </div>
                                        <div className="psa-form-group">
                                            <label className="psa-form-label">Position</label>
                                            <input
                                                type="text"
                                                className="psa-form-input"
                                                placeholder="Enter Position"
                                                value={position}
                                                onChange={(e) => setPosition(e.target.value)}
                                            />
                                        </div>
                                        <button className="psa-modal-primary-btn" disabled={!canConfirm} onClick={handleConfirmApproval}>
                                            Confirm Approval
                                        </button>
                                        <button className="psa-modal-text-btn" onClick={closeModal}>Cancel</button>
                                    </>
                                )}

                                {approveStep === 2 && (
                                    <form onSubmit={handleApproveService}>
                                        <div className="psa-form-group">
                                            <label className="psa-form-label">
                                                Remark <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(Optional)</span>
                                            </label>
                                            <textarea
                                                className="psa-form-textarea"
                                                rows={3}
                                                placeholder="Add any additional comments..."
                                                value={remark}
                                                onChange={(e) => setRemark(e.target.value)}
                                            />
                                        </div>
                                        <div className="psa-form-group">
                                            <div className="psa-signature-header">
                                                <label className="psa-form-label" style={{ margin: 0 }}>Signature</label>
                                                <button type="button" className="psa-signature-clear" onClick={() => sigPad.current?.clear()}>
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                            <div className="psa-signature-box">
                                                <span className="psa-signature-hint">Sign here</span>
                                                <SignatureCanvas
                                                    ref={sigPad}
                                                    penColor="black"
                                                    canvasProps={{ width: 392, height: 150 }}
                                                />
                                                <div className="psa-signature-line" />
                                            </div>
                                        </div>
                                        <button type="submit" className="psa-modal-primary-btn" disabled={submitLoader}>
                                            {submitLoader && (
                                                <Oval visible height="16" width="16" color="#fff" secondaryColor="rgba(255,255,255,0.4)" ariaLabel="loading" />
                                            )}
                                            Confirm Approval
                                        </button>
                                        <button type="button" className="psa-modal-text-btn" onClick={() => setApproveStep(1)}>Back</button>
                                    </form>
                                )}
                            </>
                        )}

                        {/* ── CLARIFY flow ── */}
                        {decisionType === "clarify" && (
                            <form onSubmit={handleSendClarification}>
                                <p className="psa-clarification-subtitle">Ask a question before deciding</p>
                                <div className="psa-form-group">
                                    <label className="psa-form-label">Full Name</label>
                                    <input
                                        type="text"
                                        className="psa-form-input"
                                        placeholder="Enter Your Name"
                                        value={clarifyName}
                                        onChange={(e) => setClarifyName(e.target.value)}
                                    />
                                </div>
                                <div className="psa-form-group">
                                    <label className="psa-form-label">
                                        What needs clarification? <span>*</span>
                                    </label>
                                    <textarea
                                        className="psa-form-textarea"
                                        rows={4}
                                        placeholder="Describe what you need the technician to clarify"
                                        value={clarifyQuestion}
                                        onChange={(e) => setClarifyQuestion(e.target.value)}
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="psa-modal-primary-btn"
                                    disabled={!clarifyQuestion.trim() || submitLoader}
                                >
                                    {submitLoader && (
                                        <Oval visible height="16" width="16" color="#fff" secondaryColor="rgba(255,255,255,0.4)" ariaLabel="loading" />
                                    )}
                                    Send Clarification Request
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            <DocumentViewerModal
                isOpen={docViewer.open}
                onClose={closeDocViewer}
                fileUrl={docViewer.url}
                fileType={docViewer.type}
                fileName={docViewer.name}
            />
        </>
    );
};

export default PublicServiceAssetApproval;